import axios from "axios";
import { providerSyncEndpoint } from "../utils/pharmacies";

// Get the API URL from environment variables with fallbacks

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD_ENV === "production"
    ? "https://api.lfrxportal.com"
    : "http://localhost:3000");

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Export the api instance directly
export { api };

// Add this function to your api.js file

// Add a request interceptor to include auth token in headers
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("authToken");

    // Always add Authorization header with token if it exists
    if (token) {
      config.headers["Authorization"] = `Token ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      // Clear auth data
      localStorage.removeItem("authToken");
      localStorage.removeItem("userInfo");

      // Redirect to login page if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export const deleteOrder = async (orderId) => {
  try {
    console.log(`Deleting order ${orderId}`);
    const response = await api.delete(`/api/orders/${orderId}/`);
    console.log("Order deletion response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting order:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to delete order");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const fetchOrders = async (
  page = 1,
  statusFilter = "all",
  search = "",
  sort = "",
  pageSize = 25,
  { startDate, endDate, pharmacyDeliveryStatus } = {}
) => {
  try {
    // Build query parameters
    const params = new URLSearchParams();

    params.append("page_size", pageSize);
    // params.append("offset", (page - 1) * pageSize);
    params.append("page", page);

    // Handle status filter
    if (statusFilter && statusFilter !== "all") {
      params.append("status", statusFilter);
    }

    // Handle search
    if (search) {
      params.append("search", search);
    }

    // Handle sorting: the API reads DRF's `ordering` param, with the page's
    // field names mapped onto the model paths it accepts
    if (sort) {
      const desc = sort.startsWith("-");
      const field = desc ? sort.slice(1) : sort;
      const orderingFields = {
        patient_name: "patient__last_name",
        patient_dob: "patient__date_of_birth"
      };
      params.append("ordering", `${desc ? "-" : ""}${orderingFields[field] || field}`);
    }

    // Handle date range filters
    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }

    // Handle pharmacy delivery status filter
    if (pharmacyDeliveryStatus && pharmacyDeliveryStatus !== "all") {
      params.append("pharmacy_delivery_status", pharmacyDeliveryStatus);
    }

    const response = await api.get(`/api/orders/?${params.toString()}`);

    // The API returns data in this format, so we return it directly
    return response.data;
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw error;
  }
};

export const fetchDashboardStats = async (
  startDate = "2025-04-01",
  endDate = "2025-04-30",
  minPrice = null,
  maxPrice = null
) => {
  try {
    console.log("Fetching dashboard statistics...");

    // Build params object, only including price params if they exist
    const params = {
      start_date: startDate,
      end_date: endDate,
    };

    if (minPrice !== null && minPrice !== undefined) {
      params.min_price = minPrice;
    }

    if (maxPrice !== null && maxPrice !== undefined) {
      params.max_price = maxPrice;
    }

    const response = await api.get("/api/dashboard/order/stats/", {
      params,
    });

    console.log("Dashboard stats API response:", response.data);

    return {
      matched_patients: response.data.matched_patients || 0,
      user_breakdown: response.data.user_breakdown || 0,
      medication_breakdown: response.data.medication_breakdown || 0,
      order_type_breakdown: response.data.order_type_breakdown || 0,
      variant_breakdown: response.data.variant_breakdown || 0,
      survey_frequency: response.data.survey_frequency || 0,
      state_breakdown: response.data.state_breakdown || 0,
      total_statistics: response.data.total_statistics || 0,
      pharmacy_breakdown: response.data.pharmacy_breakdown || 0,
      dashboardData: response.dashboardData || 0,

      unmatched_patients: response.data.unmatched_patients || 0,
      total_patients: response.data.total_patients || 0,
      total_orders: response.data.total_orders || 0,
      total_surveys: response.data.total_surveys || 0,
      patient_change: response.data.patient_change || 0,
      order_change: response.data.order_change || 0,
      survey_change: response.data.survey_change || 0,
      side_effect_rate: response.data.side_effect_rate || 0,
      average_effectiveness: response.data.average_effectiveness || 0,
      chart_data: response.data.chart_data || [],
    };
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    // Return default values in case of error
    return {
      user_breakdown: 0,
      order_type_breakdown: 0,
      medication_breakdown: 0,
      variant_breakdown: 0,
      survey_frequency: 0,
      total_statistics: 0,
      matched_patients: 0,
      pharmacy_breakdown: 0,
      unmatched_patients: 0,
      state_breakdown: 0,
      total_patients: 0,
      total_orders: 0,
      total_surveys: 0,
      patient_change: 0,
      order_change: 0,
      survey_change: 0,
      side_effect_rate: 0,
      average_effectiveness: 0,
      chart_data: [],
    };
  }
};

export const completeOrder = async (orderId) => {
  try {
    console.log(`Completing order ${orderId}`);
    const response = await api.patch(`/api/orders/${orderId}/`, {
      status: "completed",
    });
    console.log("Order completion response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error completing order:", error);

    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);

      // Handle 409 conflict error specifically
      if (error.response.status === 409) {
        // For 409 errors, throw the actual error response data instead of a generic message
        const errorData = error.response.data;
        const conflictError = new Error(
          errorData.message || "Order is already completed"
        );
        conflictError.status = 409;
        conflictError.data = errorData;
        throw conflictError;
      }

      // For other errors, use the existing logic
      throw new Error(error.response.data.error || "Failed to complete order");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

// State management functions
export const fetchStates = async () => {
  try {
    console.log("Fetching states from API...");
    const response = await api.get("/api/states/");
    console.log("States API raw response:", response);
    console.log("States API response data type:", typeof response.data);
    console.log("States API response data:", response.data);

    // Ensure consistent data structure
    let statesData = Array.isArray(response.data)
      ? response.data
      : response.data.results || [];
    console.log("States data after initial processing:", statesData);

    // Log the first state to understand its structure
    if (statesData.length > 0) {
      console.log("First state example:", statesData[0]);
      console.log("State properties:", Object.keys(statesData[0]));
    }

    // Map the data to ensure consistent property names
    statesData = statesData.map((state, index) => {
      const processedState = {
        id: state.id || index + 1, // Ensure every state has an ID
        state_code: state.state_code || state.code || "",
        state_name: state.state_name || state.name || "",
        // Keep original properties for backward compatibility
        ...state,
      };
      console.log(`Processed state ${index}:`, processedState);
      return processedState;
    });

    console.log("Final processed states data:", statesData);
    return statesData;
  } catch (error) {
    console.error("Error fetching states:", error);
    return [];
  }
};

// Fetch pharmacies with optional state filtering
export const fetchPharmacies = async (params = {}) => {
  try {
    const { state_id } = params;
    let url = "/api/pharmacies/";

    // Add state_id filter if provided
    if (state_id) {
      url += `?state_id=${state_id}`;
    }

    console.log(`Fetching pharmacies with URL: ${url}`);
    const response = await api.get(url);
    console.log("Pharmacies API response:", response.data);
    return Array.isArray(response.data)
      ? response.data
      : response.data.results || [];
  } catch (error) {
    console.error("Error fetching pharmacies:", error);
    throw error;
  }
};

// Fetch medicines with optional pharmacy and state filtering
export const fetchMedicines = async (params = {}) => {
  try {
    const { pharmacy_id, state_id } = params;
    let url = "/api/medicines/";
    const queryParams = [];

    if (pharmacy_id) {
      queryParams.push(`pharmacy_id=${pharmacy_id}`);
    }

    if (state_id) {
      queryParams.push(`state_id=${state_id}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    console.log(`Fetching medicines with URL: ${url}`);
    const response = await api.get(url);
    console.log("Medicines API response:", response.data);
    return Array.isArray(response.data)
      ? response.data
      : response.data.results || [];
  } catch (error) {
    console.error("Error fetching medicines:", error);
    throw error;
  }
};

// Aliases for backward compatibility
export const fetchMedications = fetchMedicines;
export const getMedicines = fetchMedicines;
export const getStates = fetchStates;
export const getPharmacies = fetchPharmacies;

export const fetchPatients = async (params = {}) => {
  try {
    const {
      page = 1,
      search = "",
      sortBy = "last_name",
      sortOrder = "asc",
    } = params;

    // Construct query parameters
    const queryParams = new URLSearchParams();
    if (page) queryParams.append("page", page);
    if (search) queryParams.append("search", search);
    if (sortBy) queryParams.append("sort_by", sortBy);
    if (sortOrder) queryParams.append("sort_order", sortOrder);

    const url = `/api/patients/?${queryParams.toString()}`;
    console.log("Fetching patients with URL:", url);

    const response = await api.get(url);
    console.log("Patients API response:", response.data);

    // Return both results and pagination information
    return {
      patients: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next,
      previous: response.data.previous,
      currentPage: parseInt(page, 10),
    };
  } catch (error) {
    console.error("Error fetching patients:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }

    // Return empty data structure
    return {
      patients: [],
      count: 0,
      next: null,
      previous: null,
      currentPage: parseInt(page, 10),
    };
  }
};

export const searchPatientByPhone = async (phone) => {
  try {
    console.log(`Searching for patient with phone: ${phone}`);
    const response = await api.get(`/api/patients/?phone=${phone}`);
    console.log("Patient search response:", response.data);

    // The API returns data in a structure with results field
    if (
      response.data &&
      response.data.results &&
      response.data.results.length > 0
    ) {
      return response.data.results; // Return the results array
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error searching for patient:", error);
    return null;
  }
};

export const fetchPatientByPhone = searchPatientByPhone;

export const fetchPatientDetail = async (patientId) => {
  try {
    const response = await api.get(`/api/patients/${patientId}/`);
    console.log("Patient detail response:", response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching patient ${patientId}:`, error);
    throw error;
  }
};


export const updatePatient = async (patientId, payload) => {
  try {
    const response = await api.patch(`/api/patients/${patientId}/update-patient/`, payload);
    return response.data;
  } catch (error) {
    console.error("Update patient error:", error);
    throw error;
  }
};

// Alias for consistency with component naming
export const getOrders = fetchOrders;

export const getOrderByIdPublic = async (orderId) => {
  try {
    console.log(`Fetching order with ID: ${orderId}`);

    // Create a new axios instance without the interceptors for this specific call
    const publicApi = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    const response = await publicApi.get(`/api/orders/get-order-details/`, {
      params: { unique_id: orderId },
    });

    console.log(`Order API response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching order with ID ${orderId}:`, error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

export const getOrderById = async (orderId) => {
  try {
    console.log(`Fetching order with ID: ${orderId}`);
    const response = await api.get(`/api/orders/${orderId}/`);
    console.log(`Order API response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching order with ID ${orderId}:`, error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

export const getOrderByUUId = async (orderId) => {
  try {
    console.log(`Fetching order with ID: ${orderId}`);
    const response = await api.get(`/api/orders/${orderId}/`);
    console.log(`Order API response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching order with ID ${orderId}:`, error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

export const fetchOrderById = getOrderById;

export const updateOrderStatus = async (orderId, newStatus) => {
  try {
    console.log(`Updating order ${orderId} status to: ${newStatus}`);
    const response = await api.patch(`/api/orders/${orderId}/`, {
      status: newStatus,
    });
    console.log("Order status update response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating order status:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(
        error.response.data.error || "Failed to update order status"
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const createOrder = async (orderData) => {
  try {
    console.log("Creating order with data:", orderData);
    const response = await api.post("/api/orders/", orderData);
    console.log("Order creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating order:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to create order");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const updateOrder = async (orderId, orderData) => {
  try {
    console.log(`Updating order ${orderId} with data:`, orderData);
    // Add additional logging for patient_id and pharmacy_id
    console.log(
      `Patient ID: ${orderData.patient_id}, Pharmacy ID: ${orderData.pharmacy_id}`
    );

    // Fixed URL path to match the backend route
    const response = await api.put(
      `/api/orders/${orderId}/update-with-items/`,
      orderData
    );
    console.log("Order update response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating order:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to update order");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const fetchSurveys = async (params = {}) => {
  try {
    const {
      page = 1,
      search = "",
      sortBy = "created_at",
      sortOrder = "desc",
    } = params;

    // Construct query parameters
    const queryParams = new URLSearchParams();
    if (page) queryParams.append("page", page);
    if (search) queryParams.append("search", search);
    if (sortBy) queryParams.append("sort_by", sortBy);
    if (sortOrder) queryParams.append("sort_order", sortOrder);

    const url = `/api/surveys/?${queryParams.toString()}`;
    console.log("Fetching surveys with URL:", url);

    const response = await api.get(url);
    console.log("Surveys API response:", response.data);

    // Return both results and pagination information
    return {
      surveys: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next,
      previous: response.data.previous,
      currentPage: parseInt(page, 10),
    };
  } catch (error) {
    console.error("Error fetching surveys:", error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }

    // Return empty data structure
    return {
      surveys: [],
      count: 0,
      next: null,
      previous: null,
      currentPage: parseInt(page, 10),
    };
  }
};

// Alias for consistency with component naming
export const getSurveys = fetchSurveys;

// Get a single survey by ID
export const getSurvey = async (surveyId) => {
  try {
    console.log(`Fetching survey with ID: ${surveyId}`);
    const response = await api.get(`/api/surveys/${surveyId}/`);
    console.log(`Survey API response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching survey with ID ${surveyId}:`, error);
    // Show detailed error information
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error message:", error.message);
    }
    throw error;
  }
};

// export const fetchDashboardStats = async () => {
//   try {
//     const patientsData = await fetchPatients();
//     const medications = await fetchMedications();
//     const orders = await fetchOrders();
//     const surveysData = await fetchSurveys();

//     return {
//       patientsCount: patientsData.count || 0,
//       medicationsCount: medications.length,
//       ordersCount: orders.length,
//       surveysCount: surveysData.count || surveysData.surveys?.length || 0
//     };
//   } catch (error) {
//     console.error("Error fetching dashboard stats:", error);
//     throw error;
//   }
// };

// Pharmacy management functions
export const createPharmacy = async (pharmacyData) => {
  try {
    console.log("Creating pharmacy with data:", pharmacyData);
    const response = await api.post("/api/pharmacies/", pharmacyData);
    console.log("Pharmacy creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating pharmacy:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to create pharmacy");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const updatePharmacy = async (pharmacyId, pharmacyData) => {
  try {
    console.log(
      `Updating pharmacy ${pharmacyId} with data:`,
      JSON.stringify(pharmacyData, null, 2)
    );

    // Make sure pharmacyId is a number
    const id = parseInt(pharmacyId);
    if (isNaN(id)) {
      console.error("Invalid pharmacy ID for update:", pharmacyId);
      throw new Error("Invalid pharmacy ID");
    }

    // Ensure we're using PUT method with the correct endpoint
    const endpoint = `/api/pharmacies/${id}/`;
    console.log("Using update endpoint:", endpoint);

    const response = await api.put(endpoint, pharmacyData);
    console.log("Pharmacy update response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating pharmacy:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      console.error("Request config:", error.response.config);
      throw new Error(error.response.data.error || "Failed to update pharmacy");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const deletePharmacy = async (pharmacyId) => {
  try {
    console.log(`Deleting pharmacy ${pharmacyId}`);
    const response = await api.delete(`/api/pharmacies/${pharmacyId}/`);
    console.log("Pharmacy deletion response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting pharmacy:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to delete pharmacy");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

// Medicine management functions
export const createMedicine = async (medicineData) => {
  try {
    console.log(`Creating new medicine with data:`, medicineData);

    // Clean up the data to match the updated backend model
    const cleanedData = {
      medicine_name: medicineData.medicine_name,
      medicine_sku: medicineData.medicine_sku,
      description: medicineData.description,
      pharmacies: medicineData.pharmacies,
      states: medicineData.states,
      variants: medicineData.variants,
      dosage_instructions: medicineData.dosage_instructions,
    };

    console.log("Sending cleaned data to API:", cleanedData);

    const response = await api.post(
      "/api/medicines/create-with-variants/",
      cleanedData
    );
    console.log("Medicine creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating medicine:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to create medicine");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const updateMedicine = async (medicineId, medicineData) => {
  try {
    console.log(`Updating medicine ${medicineId} with data:`, medicineData);

    // Prepare the payload for the update-complete endpoint
    const updatePayload = {
      medicine_name: medicineData.medicine_name,
      medicine_sku: medicineData.medicine_sku,
      description: medicineData.description,
      pharmacies: medicineData.pharmacies,
      states: medicineData.states,
      variants: medicineData.variants.map((variant) => ({
        // Only include variant_id if it exists and is not a temporary id
        ...(variant.variant_id &&
        !variant.variant_id.toString().startsWith("temp-")
          ? { variant_id: variant.variant_id }
          : {}),
        quantity: variant.quantity,
        unit: variant.unit,
        note: variant.note || "",
      })),

      dosage_instructions_new: [],
      dosage_instructions_update: [],
    };

    // Process dosage instructions
    if (
      medicineData.dosage_instructions &&
      Array.isArray(medicineData.dosage_instructions)
    ) {
      // Separate existing and new dosage instructions
      updatePayload.dosage_instructions_update =
        medicineData.dosage_instructions
          .filter((instruction) => instruction.dosage_id)
          .map((instruction) => ({
            dosage_id: instruction.dosage_id,
            dosage_instruction: instruction.dosage_instruction,
          }));

      updatePayload.dosage_instructions_new = medicineData.dosage_instructions
        .filter((instruction) => !instruction.dosage_id)
        .map((instruction) => ({
          dosage_instruction: instruction.dosage_instruction,
        }));
    }

    console.log("Sending update-complete payload to API:", updatePayload);

    const response = await api.put(
      `/api/medicines/${medicineId}/update-complete/`,
      updatePayload
    );

    // Log the response status to help with debugging
    console.log("Medicine update response status:", response.status);
    console.log("Medicine update-complete response:", response.data);

    // Handle both 200 and 201 responses as successful
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }

    // If we get here, something unexpected happened
    throw new Error(`Unexpected response status: ${response.status}`);
  } catch (error) {
    console.error("Error updating medicine:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to update medicine");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

export const deleteMedicine = async (medicineId) => {
  try {
    console.log(`Deleting medicine ${medicineId}`);
    const response = await api.delete(`/api/medicines/${medicineId}/`);
    console.log("Medicine deletion response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting medicine:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || "Failed to delete medicine");
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

/**
 * Add a dosage instruction to a medicine
 * @param {number} medicineId - The ID of the medicine
 * @param {string} dosageInstruction - The dosage instruction to add
 * @returns {Promise<Object>} - The created dosage instruction with its ID
 */
export const addDosageInstruction = async (medicineId, dosageInstruction) => {
  try {
    console.log(
      `Adding dosage instruction to medicine ${medicineId}:`,
      dosageInstruction
    );
    const response = await api.post(
      `/api/medicines/${medicineId}/add-dosage-instruction/`,
      { dosage_instruction: dosageInstruction }
    );
    console.log("Dosage instruction addition response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding dosage instruction:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(
        error.response.data.error || "Failed to add dosage instruction"
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

/**
 * Add a new address for a patient
 * @param {number} patientId - The ID of the patient
 * @param {Object} addressData - The address data to add
 * @returns {Promise<Object>} - The created address
 */
export const addPatientAddress = async (patientId, addressData) => {
  try {
    const response = await api.post(
      `/api/patients/${patientId}/add-address/`,
      addressData
    );
    console.log("Patient address addition response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding patient address:", error);
    throw error;
  }
};

// Fetch pharmacies and medicines by state in a single request
export const fetchPharmaciesAndMedicines = async (states) => {
  try {
    console.log("Fetching pharmacies and medicines for states:", states);

    // Convert single state to array if needed
    const statesList = Array.isArray(states) ? states : [states];

    const response = await api.post("/api/pharmacies-and-medicines/", {
      states: statesList,
    });

    console.log("Pharmacies and medicines response:", response.data);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching pharmacies and medicines:", error);
    return [];
  }
};

/**
 * Import a Miller's address for a patient
 * @param {number} patientId - The ID of the patient
 * @param {number} millersAddressId - The ID of the Miller's address to import
 * @returns {Promise<Object>} - The imported address data
 */
export const importMillersAddress = async (patientId, millersAddressId) => {
  try {
    console.log(
      `Importing Miller's address ${millersAddressId} for patient ${patientId}`
    );
    const response = await api.post(
      `/api/patients/${patientId}/import-millers-address/`,
      {
        millers_address_id: millersAddressId,
      }
    );
    console.log("Miller's address import response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error importing Miller's address:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(
        error.response.data.error || "Failed to import Miller's address"
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error message:", error.message);
      throw error;
    }
  }
};

/**
 * Sync a patient's addresses from a specific pharmacy's API
 * @param {number} patientId - The ID of the patient
 * @param {number} pharmacyId - The ID of the pharmacy to sync addresses from
 * @returns {Promise<Object>} - { supported, addresses, pharmacy_name, ... }
 */
export const syncPharmacyAddresses = async (patientId, pharmacyId) => {
  try {
    const response = await api.get(
      `/api/patients/${patientId}/sync-pharmacy-addresses/`,
      { params: { pharmacy_id: pharmacyId } }
    );
    console.log("Pharmacy address sync response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error syncing pharmacy addresses:", error);
    if (error.response) {
      throw new Error(
        error.response.data.error || "Failed to sync pharmacy addresses"
      );
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw error;
    }
  }
};

/**
 * Import a pharmacy address (Millers or Nie's) into the patient's addresses
 * @param {number} patientId - The ID of the patient
 * @param {number} pharmacyId - The ID of the pharmacy the address belongs to
 * @param {number} addressId - The ID of the pharmacy address to import
 * @returns {Promise<Object>} - { message, imported, address, addresses }
 */
export const importPharmacyAddress = async (patientId, pharmacyId, addressId) => {
  try {
    const response = await api.post(
      `/api/patients/${patientId}/import-pharmacy-address/`,
      {
        pharmacy_id: pharmacyId,
        address_id: addressId,
      }
    );
    console.log("Pharmacy address import response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error importing pharmacy address:", error);
    if (error.response) {
      throw new Error(
        error.response.data.error || "Failed to import pharmacy address"
      );
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw error;
    }
  }
};

// Provider management functions

/**
 * Fetch all providers with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Paginated providers response
 */
export const fetchProviders = async (params = {}) => {
  try {
    const {
      page = 1,
      search = "",
      state_id = null,
      pharmacy_id = null,
      is_active = null,
      is_global = null,
      credential = "",
      ordering = ""
    } = params;

    const queryParams = new URLSearchParams();
    if (page) queryParams.append("page", page);
    if (search) queryParams.append("search", search);
    if (state_id) queryParams.append("state_id", state_id);
    if (pharmacy_id) queryParams.append("pharmacy_id", pharmacy_id);
    if (is_active !== null) queryParams.append("is_active", is_active);
    if (is_global !== null) queryParams.append("is_global", is_global);
    if (credential) queryParams.append("credential", credential);
    if (ordering) queryParams.append("ordering", ordering);

    const url = `/api/providers/?${queryParams.toString()}`;
    console.log("Fetching providers with URL:", url);

    const response = await api.get(url);
    console.log("Providers API response:", response.data);

    return {
      providers: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next,
      previous: response.data.previous,
      currentPage: parseInt(page, 10)
    };
  } catch (error) {
    console.error("Error fetching providers:", error);
    throw error;
  }
};

/**
 * Fetch a single provider by ID
 * @param {number} providerId - The provider ID
 * @returns {Promise<Object>} - Provider details
 */
export const fetchProviderById = async (providerId) => {
  try {
    console.log(`Fetching provider with ID: ${providerId}`);
    const response = await api.get(`/api/providers/${providerId}/`);
    console.log("Provider detail response:", response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching provider ${providerId}:`, error);
    throw error;
  }
};

/**
 * Create a new provider
 * @param {Object} providerData - Provider data to create
 * @returns {Promise<Object>} - Created provider
 */
export const createProvider = async (providerData) => {
  try {
    console.log("Creating provider with data:", providerData);
    const response = await api.post("/api/providers/", providerData);
    console.log("Provider creation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to create provider");
    }
    throw error;
  }
};

/**
 * Update a provider
 * @param {number} providerId - Provider ID
 * @param {Object} providerData - Updated provider data
 * @returns {Promise<Object>} - Updated provider
 */
export const updateProvider = async (providerId, providerData) => {
  try {
    console.log(`Updating provider ${providerId} with data:`, providerData);
    const response = await api.patch(`/api/providers/${providerId}/`, providerData);
    console.log("Provider update response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to update provider");
    }
    throw error;
  }
};

/**
 * Delete a provider
 * @param {number} providerId - Provider ID to delete
 * @returns {Promise<void>}
 */
export const deleteProvider = async (providerId) => {
  try {
    console.log(`Deleting provider ${providerId}`);
    await api.delete(`/api/providers/${providerId}/`);
    console.log("Provider deleted successfully");
  } catch (error) {
    console.error("Error deleting provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to delete provider");
    }
    throw error;
  }
};

/**
 * Assign multiple states to a provider (replaces all existing states)
 * @param {number} providerId - Provider ID
 * @param {Array<number>} stateIds - Array of state IDs
 * @returns {Promise<Object>} - Updated provider
 */
export const assignProviderStates = async (providerId, stateIds) => {
  try {
    console.log(`Assigning states to provider ${providerId}:`, stateIds);
    const response = await api.post(`/api/providers/${providerId}/assign-states/`, {
      state_ids: stateIds
    });
    console.log("Assign states response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error assigning states to provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to assign states");
    }
    throw error;
  }
};

/**
 * Add a single state to a provider
 * @param {number} providerId - Provider ID
 * @param {Object} stateData - State data to add
 * @returns {Promise<Object>} - Updated provider
 */
export const addProviderState = async (providerId, stateData) => {
  try {
    console.log(`Adding state to provider ${providerId}:`, stateData);
    const response = await api.post(`/api/providers/${providerId}/add-state/`, stateData);
    console.log("Add state response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding state to provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to add state");
    }
    throw error;
  }
};

/**
 * Remove a state from a provider
 * @param {number} providerId - Provider ID
 * @param {number} stateId - State ID to remove
 * @returns {Promise<Object>} - Updated provider
 */
export const removeProviderState = async (providerId, stateId) => {
  try {
    console.log(`Removing state ${stateId} from provider ${providerId}`);
    const response = await api.post(`/api/providers/${providerId}/remove-state/`, {
      state_id: stateId
    });
    console.log("Remove state response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing state from provider:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to remove state");
    }
    throw error;
  }
};

/**
 * Sync providers from Millers API
 * @returns {Promise<Object>} - Sync result
 */
export const syncProviders = async (pharmacyId = null, pharmacyName = '') => {
  try {
    const endpoint = providerSyncEndpoint(pharmacyName);
    console.log(`Syncing providers via ${endpoint}`, pharmacyId ? `pharmacy_id: ${pharmacyId}` : 'all');
    const body = pharmacyId ? { pharmacy_id: pharmacyId } : {};
    const response = await api.post(endpoint, body);
    console.log("Provider sync response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error syncing providers:", error);
    if (error.response) {
      console.error("Error status:", error.response.status);
      console.error("Error data:", error.response.data);
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to sync providers");
    }
    throw error;
  }
};

/**
 * Get provider sync history
 * @returns {Promise<Array>} - Sync logs
 */
export const fetchProviderSyncLogs = async () => {
  try {
    console.log("Fetching provider sync logs...");
    const response = await api.get("/api/providers/sync-logs/");
    console.log("Sync logs response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching sync logs:", error);
    return [];
  }
};

/**
 * Fetch providers licensed in a given state abbreviation (e.g., FL, TX)
 * @param {string} stateAbbr - Two-letter state abbreviation
 * @returns {Promise<{state: string, count: number, providers: Array}>}
 */
export const fetchProvidersByState = async (stateAbbr) => {
  try {
    if (!stateAbbr) {
      return { state: "", count: 0, providers: [] };
    }
    const normalized = stateAbbr.toString().trim().toUpperCase();
    console.log(`Fetching providers by state: ${normalized}`);
    const response = await api.get(`/api/providers/by-state/${normalized}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching providers by state:", error);
    if (error.response) {
      throw new Error(error.response.data.error || error.response.data.detail || "Failed to fetch providers");
    }
    throw error;
  }
};

// Aliases for provider functions
export const getProviders = fetchProviders;
export const getProviderById = fetchProviderById;
export const getProvidersByState = fetchProvidersByState;

// Export default for backward compatibility
export default api;
